import { useQuery } from "@apollo/react-hooks";
import { Col, Row } from "atoms/grid-new";
import { QueryError } from "atoms/QueryError";
import { H1, PSmall, RichText } from "atoms/typography";
import BlogContainer from "components/Blog/BlogContainer";
import BlogLink from "components/Blog/BlogLink";
import largeDefault from "components/Blog/penfold-banner-lg.png";
import mediumDefault from "components/Blog/penfold-banner-md.png";
import smallDefault from "components/Blog/penfold-banner-sm.png";
import Hero from "components/Hero";
import Loading from "components/Loading";
import { format } from "date-fns";
import gqlPrismic from "graphql-tag";
import { formatMenu, MAIN_MENU_QUERY, PublicLayout } from "layouts/Public";
import Head from "next/head";
import { useRouter } from "next/router";

const GET_BLOG_POST = gqlPrismic`
  query Post($uid: String!) {
    blog_post(uid: $uid, lang: "en-gb") {
      title
      images
      content
      publish_date_override
      author {
        ... on Author {
          name
        }
      }
      _meta {
        firstPublicationDate
        lastPublicationDate
      }
    }
  }
`;

const defaultImages = {
  small: smallDefault,
  medium: mediumDefault,
  large: largeDefault,
};

const formatDate = (date: string | Date) => {
  return format(new Date(date), `iiii do MMMM, yyyy`);
};

const getBlogBannerImages = (images) => {
  if (images) {
    return {
      small: images.small.url,
      medium: images.medium.url,
      large: images.url,
      dimensions: {
        width: images.dimensions.width,
        height: images.dimensions.height,
      },
    };
  }
  return {
    ...defaultImages,
    dimensions: {
      // Taken from the `medium` default image
      width: 1536,
      height: 1152,
    },
  };
};

const Post = () => {
  const router = useRouter();
  const uid = router.query.uid;
  const pathname = router.asPath;

  const { loading, error, data } = useQuery(GET_BLOG_POST, {
    variables: { uid },
    context: {
      endpoint: "prismic",
    },
  });

  const menu = useQuery(MAIN_MENU_QUERY, {
    context: {
      endpoint: "prismic",
    },
  });

  if (error || menu.error) return <QueryError error={error} />;
  if (loading || menu.loading) return <Loading />;

  const navProps = menu?.data?.allMain_menus?.edges[0]?.node;
  const mainMenu = navProps.nav
    ? formatMenu(navProps)
    : { content: [], options: {} };

  if (!data.blog_post && process.browser) {
    router.push("/404");
  }

  const images = getBlogBannerImages(data.blog_post.images);

  const title = data.blog_post.title[0].text;
  const author = data.blog_post.author.name;
  const publishDateOverride = data.blog_post.publish_date_override;
  const dateToShow = publishDateOverride
    ? publishDateOverride
    : data.blog_post._meta.firstPublicationDate;
  const date = formatDate(dateToShow);
  const content = data.blog_post.content;

  const postPageMeta = {
    title: title,
    ogAuthor: author,
    ogPublishedTime: dateToShow,
    ogType: "article",
    ogImage: images.medium,
    ogUrl: `https://getpenfold.com${pathname}`,
    twitterCreator: "@pete_hykin",
  };

  return (
    <>
      <StructuredData
        headline={title}
        datePublished={dateToShow}
        dateModified={data.blog_post._meta.firstPublicationDate}
        authorName={author}
        image={{
          url: images.medium,
          width: images.dimensions.width,
          height: images.dimensions.height,
        }}
      />
      <PublicLayout metadata={postPageMeta} mainMenu={mainMenu}>
        <Hero images={images} />
        <BlogContainer pb={[60, , 80]}>
          <Row>
            <Col span={[12, , , 4]} pr={[, , , 24, 0]}>
              <H1>{title}</H1>
              <PSmall>
                {author} <br /> {date}
              </PSmall>
            </Col>
            <Col span={[12, , , 8]}>
              <RichText render={content} />
            </Col>
          </Row>
          <BlogLink href="/news">Back to Blog</BlogLink>
        </BlogContainer>
      </PublicLayout>
    </>
  );
};

export default Post;

interface StructuredDataProps {
  headline: string;
  datePublished: Date;
  dateModified?: Date;
  authorName: string;
  image: {
    url: string;
    height: number;
    width: number;
  };
}
const StructuredData = ({
  headline,
  datePublished,
  dateModified,
  authorName,
  image,
}: StructuredDataProps) => {
  const structuredData = createStructuredData({
    headline,
    datePublished,
    dateModified,
    authorName,
    image,
  });

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: structuredData }}
      />
    </Head>
  );
};

const createStructuredData = ({
  headline,
  datePublished,
  dateModified,
  authorName,
  image,
}: StructuredDataProps) => {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://getpenfold.com/news",
    },
    headline,
    image: {
      "@type": "imageObject",
      ...image,
    },
    datePublished,
    dateModified,
    author: {
      "@type": "Person",
      name: authorName,
    },
    publisher: {
      "@type": "Organization",
      name: "Penfold",
    },
  });
};
